/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import { findTreeConfigPda } from '../pdas';
import { MPL_BUBBLEGUM_PROGRAM_ADDRESS } from '../programs';
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from '../shared';

export const SET_TREE_DELEGATE_DISCRIMINATOR = new Uint8Array([
  253, 118, 66, 37, 190, 49, 154, 102,
]);

export function getSetTreeDelegateDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SET_TREE_DELEGATE_DISCRIMINATOR
  );
}

export type SetTreeDelegateInstruction<
  TProgram extends string = typeof MPL_BUBBLEGUM_PROGRAM_ADDRESS,
  TAccountTreeConfig extends string | AccountMeta<string> = string,
  TAccountTreeCreator extends string | AccountMeta<string> = string,
  TAccountNewTreeDelegate extends string | AccountMeta<string> = string,
  TAccountMerkleTree extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends
    | string
    | AccountMeta<string> = '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountTreeConfig extends string
        ? WritableAccount<TAccountTreeConfig>
        : TAccountTreeConfig,
      TAccountTreeCreator extends string
        ? ReadonlySignerAccount<TAccountTreeCreator> &
            AccountSignerMeta<TAccountTreeCreator>
        : TAccountTreeCreator,
      TAccountNewTreeDelegate extends string
        ? ReadonlyAccount<TAccountNewTreeDelegate>
        : TAccountNewTreeDelegate,
      TAccountMerkleTree extends string
        ? ReadonlyAccount<TAccountMerkleTree>
        : TAccountMerkleTree,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type SetTreeDelegateInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type SetTreeDelegateInstructionDataArgs = {};

export function getSetTreeDelegateInstructionDataEncoder(): FixedSizeEncoder<SetTreeDelegateInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({ ...value, discriminator: SET_TREE_DELEGATE_DISCRIMINATOR })
  );
}

export function getSetTreeDelegateInstructionDataDecoder(): FixedSizeDecoder<SetTreeDelegateInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getSetTreeDelegateInstructionDataCodec(): FixedSizeCodec<
  SetTreeDelegateInstructionDataArgs,
  SetTreeDelegateInstructionData
> {
  return combineCodec(
    getSetTreeDelegateInstructionDataEncoder(),
    getSetTreeDelegateInstructionDataDecoder()
  );
}

export type SetTreeDelegateAsyncInput<
  TAccountTreeConfig extends string = string,
  TAccountTreeCreator extends string = string,
  TAccountNewTreeDelegate extends string = string,
  TAccountMerkleTree extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  treeConfig?: Address<TAccountTreeConfig>;
  treeCreator: TransactionSigner<TAccountTreeCreator>;
  newTreeDelegate: Address<TAccountNewTreeDelegate>;
  merkleTree: Address<TAccountMerkleTree>;
  systemProgram?: Address<TAccountSystemProgram>;
};

export async function getSetTreeDelegateInstructionAsync<
  TAccountTreeConfig extends string,
  TAccountTreeCreator extends string,
  TAccountNewTreeDelegate extends string,
  TAccountMerkleTree extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof MPL_BUBBLEGUM_PROGRAM_ADDRESS,
>(
  input: SetTreeDelegateAsyncInput<
    TAccountTreeConfig,
    TAccountTreeCreator,
    TAccountNewTreeDelegate,
    TAccountMerkleTree,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress }
): Promise<
  SetTreeDelegateInstruction<
    TProgramAddress,
    TAccountTreeConfig,
    TAccountTreeCreator,
    TAccountNewTreeDelegate,
    TAccountMerkleTree,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? MPL_BUBBLEGUM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    treeConfig: { value: input.treeConfig ?? null, isWritable: true },
    treeCreator: { value: input.treeCreator ?? null, isWritable: false },
    newTreeDelegate: {
      value: input.newTreeDelegate ?? null,
      isWritable: false,
    },
    merkleTree: { value: input.merkleTree ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.treeConfig.value) {
    accounts.treeConfig.value = await findTreeConfigPda({
      merkleTree: expectAddress(accounts.merkleTree.value),
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.treeConfig),
      getAccountMeta(accounts.treeCreator),
      getAccountMeta(accounts.newTreeDelegate),
      getAccountMeta(accounts.merkleTree),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getSetTreeDelegateInstructionDataEncoder().encode({}),
    programAddress,
  } as SetTreeDelegateInstruction<
    TProgramAddress,
    TAccountTreeConfig,
    TAccountTreeCreator,
    TAccountNewTreeDelegate,
    TAccountMerkleTree,
    TAccountSystemProgram
  >);
}

export type SetTreeDelegateInput<
  TAccountTreeConfig extends string = string,
  TAccountTreeCreator extends string = string,
  TAccountNewTreeDelegate extends string = string,
  TAccountMerkleTree extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  treeConfig: Address<TAccountTreeConfig>;
  treeCreator: TransactionSigner<TAccountTreeCreator>;
  newTreeDelegate: Address<TAccountNewTreeDelegate>;
  merkleTree: Address<TAccountMerkleTree>;
  systemProgram?: Address<TAccountSystemProgram>;
};

export function getSetTreeDelegateInstruction<
  TAccountTreeConfig extends string,
  TAccountTreeCreator extends string,
  TAccountNewTreeDelegate extends string,
  TAccountMerkleTree extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof MPL_BUBBLEGUM_PROGRAM_ADDRESS,
>(
  input: SetTreeDelegateInput<
    TAccountTreeConfig,
    TAccountTreeCreator,
    TAccountNewTreeDelegate,
    TAccountMerkleTree,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress }
): SetTreeDelegateInstruction<
  TProgramAddress,
  TAccountTreeConfig,
  TAccountTreeCreator,
  TAccountNewTreeDelegate,
  TAccountMerkleTree,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? MPL_BUBBLEGUM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    treeConfig: { value: input.treeConfig ?? null, isWritable: true },
    treeCreator: { value: input.treeCreator ?? null, isWritable: false },
    newTreeDelegate: {
      value: input.newTreeDelegate ?? null,
      isWritable: false,
    },
    merkleTree: { value: input.merkleTree ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.treeConfig),
      getAccountMeta(accounts.treeCreator),
      getAccountMeta(accounts.newTreeDelegate),
      getAccountMeta(accounts.merkleTree),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getSetTreeDelegateInstructionDataEncoder().encode({}),
    programAddress,
  } as SetTreeDelegateInstruction<
    TProgramAddress,
    TAccountTreeConfig,
    TAccountTreeCreator,
    TAccountNewTreeDelegate,
    TAccountMerkleTree,
    TAccountSystemProgram
  >);
}

export type ParsedSetTreeDelegateInstruction<
  TProgram extends string = typeof MPL_BUBBLEGUM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    treeConfig: TAccountMetas[0];
    treeCreator: TAccountMetas[1];
    newTreeDelegate: TAccountMetas[2];
    merkleTree: TAccountMetas[3];
    systemProgram: TAccountMetas[4];
  };
  data: SetTreeDelegateInstructionData;
};

export function parseSetTreeDelegateInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedSetTreeDelegateInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 5) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      treeConfig: getNextAccount(),
      treeCreator: getNextAccount(),
      newTreeDelegate: getNextAccount(),
      merkleTree: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getSetTreeDelegateInstructionDataDecoder().decode(instruction.data),
  };
}
